import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, map, merge, Observable } from 'rxjs';
import { DefaultPipe } from '../../../utils/default.pipe';
import { TruncatePipe } from '../../../utils/truncate.pipe';
import { UserInfo, UserService } from '../../../utils/user.service';

/**
 * Application header component.
 */
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, RouterLink, MatMenuModule, MatTooltipModule, MatIconModule, DefaultPipe, TruncatePipe]
})
export class HeaderComponent {
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly userService = inject(UserService);

  /**
   * Current user information.
   */
  protected readonly user$: Observable<UserInfo> = merge(this.getUser(), this.userService.me());

  /**
   * Initiates user log out.
   */
  protected logout(): void {
    this.msalService.logoutRedirect();
  }

  /**
   * Retrieves current user summary.
   *
   * @returns Current user summary.
   */
  private getUser(): Observable<UserInfo> {
    return this.msalBroadcastService.inProgress$.pipe(
      takeUntilDestroyed(),
      filter(status => status === InteractionStatus.None),
      map(_ => {
        return {
          name: this.getUserName(),
          roles: []
        }
      })
    );
  }

  /**
   * Retrieves current user display name.
   *
   * @returns Current user display name.
   */
  private getUserName(): string {
    const account = this.msalService.instance.getActiveAccount();

    if (account?.idTokenClaims) {
      const givenName = account.idTokenClaims['given_name'] as string;
      const familyName = account.idTokenClaims['family_name'] as string;

      return `${givenName} ${familyName}`.trim();
    }

    return '';
  }
}
